import React, { useState } from 'react'
import i18n from 'i18next'
import * as R from 'ramda'

import Panel from '../../../../UniformComponents/Panel'
import Button from '../../../../UniformComponents/Button'
import Badge from '../../../../UniformComponents/Badge'
import Tooltip from '../../../../UniformComponents/Tooltip'
import Switch from '../../../../UniformComponents/Switch'
import Select from '../../../form/Select'
import StatusBadge from '../../../Survey/components/StatusBadge'
import Typography from '../../../../UniformComponents/Typography'

const ReportStatusBadge = ({ type, label, icon }) => (
    <div style={{ width: '100%', padding: '0 20px' }}>
        <Badge type={type} cssClass="rounded" label={i18n.t(label)} icon={icon} style={{ maxWidth: '100%' }} />
    </div>
)

const ReportStats = ({ isMaxbi, reportData }) => {
    const data = reportData.stats
    const completed = R.pathOr(0, ['aggregate', 'total', 'complete'], data)
    const screened = R.pathOr(0, ['aggregate', 'total', 'screen'], data)
    const dropped = R.pathOr(0, ['aggregate', 'total', 'drop'], data)
    const total = completed + screened + dropped

    if (isMaxbi) {
        const inProgress = R.pathOr([], ['stats', 'details', 'complete'], reportData).some((e) => e !== 0) && !total

        if (inProgress) return <ReportStatusBadge type="warning" label="inProgress" icon="fal fa-circle-xmark" />

        if (!total) return <ReportStatusBadge type="error" label="notStarted" icon="fal fa-ban" />

        return <ReportStatusBadge type="success" label="completed" icon="fal fa-circle-check" />
    }

    const statsData = [
        {
            label: 'report_dropoff_completions', value: completed, type: 'success', icon: 'fa-regular fa-check',
        },
        {
            label: 'report_dropoff_dropoffs', value: dropped, type: 'warning', icon: 'fa-regular fa-arrow-down',
        },
        {
            label: 'report_dropoff_screening_exits', value: screened, type: 'error', icon: 'fa-regular fa-ban',
        },
        {
            label: 'report_total_responses', value: total, type: null, style: { background: '#FFF' },
        },
    ]

    return (
        <div className="filterStats">
            {statsData.map(({
                label, value, type, icon, style,
            }) => (
                <div className="stat" key={label}>
                    {i18n.t(label)}
                    <Badge type={type} cssClass="rounded" label={value} icon={icon} style={style} />
                </div>
            ))}
        </div>
    )
}

const ReportOverview = ({
    reportStore,
    store,
}) => {
    const { exchangeRates } = reportStore
    const { survey, showDebug, toggleDebug } = store
    const [showAdminBox, setShowAdminBox] = useState(false)

    const isMaxbi = store.survey.isMaxbi()
    const hasPptAccess = R.pathOr(0, ['userData', 'flags', 'pptLevel'], store) > 1

    const renderDebugControl = () => {
        if (!store.isExploriAdmin()) return null
        const text = i18n.t(`${showDebug ? 'adminBox.hide' : 'adminBox.show'}`)
        const className = showDebug ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'

        return (
            <div
                className="debugContainer"
                onClick={() => setShowAdminBox((prev) => !prev)}
            >
                {showAdminBox && (
                    <Tooltip text={text} direction="left" isNew>
                        <i
                            className={className}
                            onClick={toggleDebug}
                            style={{ fontSize: '16px' }}
                        />
                    </Tooltip>
                )}
            </div>
        )
    }

    return (
        <Panel id="reportOverview">
            <div className="heading-container">
                <div className="heading-title">
                    <h3>{i18n.t(isMaxbi ? 's_tab_maxbi_responses' : 's_report_overview')}</h3>

                    {renderDebugControl()}

                    {process.env.SURVEY_STATE_REBRAND_2026 && (
                        <StatusBadge status={store.surveyStatus} />
                    )}
                </div>
                <div className="language-and-expandAll">
                    <div className="expandAll">
                        <Switch
                            label={i18n.t('report_expand_pages')}
                            state={Array.from(reportStore.openPages.values()).every((isOpen) => isOpen)}
                            onToggle={reportStore.toggleAllPages}
                        />
                    </div>
                    <div className="language-wrapper">
                        <Select
                            options={survey.metadata.locales.map((l) => ({
                                value: l,
                                label: '',
                                src: `${process.env.CDN_URL}/includes/images/flags/${l}.png`,
                            }))}
                            defaultValue={reportStore.locale}
                            onBlur={({ value }) => { reportStore.locale = value }}
                        />
                    </div>
                </div>
                <ReportStats isMaxbi={isMaxbi} reportData={reportStore.reportData} />
            </div>

            <Typography type="tiny" color="grey">{i18n.t(`report_subtext_${store.surveyStatus}`)}</Typography>

            {isMaxbi
                ? (
                    <div className="maxbi-units-container">
                        <Select
                            label={i18n.t('report_currentCurrency')}
                            options={Object.keys(exchangeRates).map((k) => ({ label: k, value: k }))}
                            onBlur={({ value }) => reportStore.changeCurrentCurrency(value)}
                            defaultValue={reportStore.currentCurrency}
                        />
                        <div
                            className="areaUnitSwitch"
                        >
                            <span>{i18n.t('refcodes_Square_Feet')}</span>
                            <Switch
                                state={reportStore.areaUnit === 'M2'}
                                onToggle={reportStore.changeAreaUnit}
                            />
                            <span>{i18n.t('refcodes_Square_Metres')}</span>
                        </div>
                    </div>
                )
                : null }

            <div className="left-panel-button-container vert">
                {survey.isMaxbi()
                    && (
                        <Button
                            cssClass="tertiary regular"
                            label={i18n.t('export_CSV')}
                            icon="fal fa-folder-arrow-down"
                            onClick={() => { store.downloadModalOpen = true }}
                        />
                    )}

                {!isMaxbi && hasPptAccess && (store.isExploriAdmin() || store.isExploriStaff()) && (
                    <Button
                        cssClass="tertiary regular"
                        label={`${i18n.t('presentationbuilder')}`}
                        onClick={() => {
                            const productId = R.pathOr('', ['metadata', 'productInfo', 'productId'], survey)
                            const url = `/presentations?event=${encodeURIComponent(productId)}`
                            window.open(url, '_blank')
                        }}
                    />
                )}
            </div>
        </Panel>
    )
}

export default ReportOverview
